/**
 * The field that opens a sensitive-access screen, and the sentence beside it.
 *
 * Shared by `/alerts`, `/incidents` and `/data-risk` rather than written three times,
 * for `docs/UI_PROCESS.md` §2's reason: a screen that hand-rolls a behaviour several
 * screens share has forked it. The sentence is the part most worth sharing — it says
 * **where the reason goes**, and a box collecting one without saying that is
 * collecting it under false pretences.
 *
 * The submit is `disabled` while the reason is too short, and this is the one place
 * on these screens where a disabled control is the honest rendering: it asserts "you
 * have not finished saying what you are doing", which is a true statement about the
 * operator. Everywhere else — an illegal transition, a delete that does not exist, an
 * acknowledgement that cannot be undone — the control is **absent**, because a greyed
 * button there would assert "the system will not let you" about something that is not
 * a permission at all.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { isHeaderSafe, isUsableReason, MAX_ACCESS_REASON, MIN_ACCESS_REASON } from './reason';

export interface AccessReasonGateProps {
  /** The reason in force. `''` means the screen has not been opened. */
  readonly value: string;
  readonly onOpen: (reason: string) => void;
  /** What is about to be read, in words — "hộp thư cảnh báo", "danh sách sự cố". */
  readonly subject: string;
  readonly testId: string;
}

export function AccessReasonGate({
  value,
  onOpen,
  subject,
  testId,
}: AccessReasonGateProps): ReactNode {
  const { t } = useI18n();
  const [draft, setDraft] = useState(value);
  const usable = isUsableReason(draft);
  // Told apart from "too short", because they are different problems with different
  // remedies and a single "invalid" message would send the operator to add words to a
  // reason that was already long enough. See `reason.ts` for why this is refused here
  // rather than encoded: an encoding this client invented would put
  // `%C4%91i%E1%BB%81u…` where a human is supposed to read a sentence.
  const unencodable = draft.trim() !== '' && !isHeaderSafe(draft.trim());
  const inputId = `${testId}-input`;

  return (
    <section className="wp-section" aria-labelledby={`${testId}-heading`} data-testid={testId}>
      <h2 className="wp-section__title" id={`${testId}-heading`}>
        {t('access.title')}
      </h2>
      <p>{t('access.why', { subject })}</p>
      <p className="wp-small wp-muted">{t('access.recorded')}</p>

      <div className="wp-field">
        <label className="wp-field__label" htmlFor={inputId}>
          {t('access.label')}
        </label>
        <input
          className="wp-field__input"
          id={inputId}
          data-testid={`${testId}-field`}
          type="text"
          value={draft}
          maxLength={MAX_ACCESS_REASON}
          autoComplete="off"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <p className="wp-small wp-muted" data-testid={`${testId}-rule`}>
          {t('access.rule', { min: MIN_ACCESS_REASON })}
        </p>
        {unencodable ? (
          <p className="wp-small" role="alert" data-testid={`${testId}-latin1`}>
            {t('access.latin1')}
          </p>
        ) : null}
      </div>

      <button
        className="wp-button wp-button--primary"
        type="button"
        data-testid={`${testId}-open`}
        disabled={!usable}
        onClick={() => {
          onOpen(draft.trim());
        }}
      >
        {value === '' ? t('access.open', { subject }) : t('access.change')}
      </button>

      {value === '' ? null : (
        <p className="wp-small wp-muted" data-testid={`${testId}-current`}>
          {t('access.current', { reason: value })}
        </p>
      )}
    </section>
  );
}
